import type { ComponentType, SVGProps } from "react"
import {
	IconCode2Outline18,
	IconMicrochipOutline18,
} from "nucleo-ui-outline-18"

import type { BrandfetchData } from "@productlane/api/src/browser"
import { cn } from "@productlane/lib/src/cn"
import { Avatar } from "@productlane/ui"
import { AgentOrb } from "@productlane/ui/src/components/agent-orb/agent-orb"

import { isApiUser } from "@/lib/api-user"
import { CompanyLogo } from "@/screens/app/customers/company-logo"

// Every notification avatar is a rounded square, like the inbox's customer avatars.
const TILE = {
	xs: { box: "h-5 w-5", icon: "h-3 w-3", radius: "rounded" },
	row: { box: "h-7 w-7", icon: "h-4 w-4", radius: "rounded-md" },
} as const

function IconTile({
	Icon,
	size,
	className,
}: {
	Icon: typeof IconCode2Outline18
	size: keyof typeof TILE
	className?: string
}) {
	return (
		<span
			className={cn(
				"flex flex-none items-center justify-center bg-gray-100 text-secondary dark:bg-gray-800",
				TILE[size].box,
				TILE[size].radius,
				className,
			)}
		>
			<Icon className={cn("flex-none transform-gpu", TILE[size].icon)} />
		</span>
	)
}

/** A person's avatar, or the code glyph for the API member, who has no face. */
export function ActorAvatar({
	user,
	size,
	className,
}: {
	user: { name?: string | null; email: string; imageUrl?: string | null }
	size: keyof typeof TILE
	className?: string
}) {
	if (isApiUser(user)) {
		return (
			<IconTile Icon={IconCode2Outline18} size={size} className={className} />
		)
	}
	return (
		<Avatar
			size={size === "xs" ? "xs" : "sm"}
			roundness="md"
			name={user.name ?? user.email}
			image={user.imageUrl}
			className={cn(TILE[size].radius, size === "row" && "h-7 w-7", className)}
		/>
	)
}

/** Stands for notifications no person triggered: agent, SLA, billing, imports. */
export function SystemActorAvatar({ size }: { size: keyof typeof TILE }) {
	return <IconTile Icon={IconMicrochipOutline18} size={size} />
}

/** A customer's face, or their initials when there is no image. */
export function CustomerAvatar({
	contact,
	className,
}: {
	contact: {
		name?: string | null
		email?: string | null
		imageUrl?: string | null
	}
	className?: string
}) {
	return (
		<Avatar
			size="sm"
			roundness="md"
			name={contact.name || contact.email || ""}
			colorSeed={contact.email}
			image={contact.imageUrl}
			className={cn("h-7 w-7", className)}
		/>
	)
}

/** The thread's company when no person is behind the event, as the Customers list draws it. */
export function CompanyAvatar({
	company,
	className,
}: {
	company: {
		name: string
		logoUrl?: string | null
		companyMeta?: { brandfetchData?: unknown } | null
	}
	className?: string
}) {
	return (
		<CompanyLogo
			size="sm"
			roundness="md"
			brandfetchData={
				company.companyMeta?.brandfetchData as BrandfetchData | undefined
			}
			linearLogoUrl={company.logoUrl || undefined}
			fallback={company.name}
			className={cn("h-7 w-7", className)}
		/>
	)
}

/** The support agent, drawn like its avatar in conversations. */
export function AgentAvatar({ className }: { className?: string }) {
	return <AgentOrb size={28} className={className} />
}

/** The event's glyph in the avatar's corner, so a face still says what happened. */
export function TypeBadge({
	Icon,
	className,
}: {
	Icon: ComponentType<SVGProps<SVGSVGElement>>
	className?: string
}) {
	return (
		<span
			className={cn(
				"absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-white text-secondary ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10",
				className,
			)}
		>
			<Icon className="h-2.5 w-2.5 flex-none transform-gpu" />
		</span>
	)
}
